import { RemitClaimModal } from "pages/EligibilityPage/RemitAccuracyLinks";
import { Button } from "primereact/button";
import { Calendar, CalendarValueType } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { MultiSelect } from "primereact/multiselect";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";
import { useMemo, useState } from "react";
import {
  useGetDistinctClearPayersQuery,
  useGetReprocessedClaimsFilterOptionsQuery,
  useGetReprocessedClaimsQuery,
} from "store/queries/claims";
import { useGetProviderOptionsByAccountQuery } from "store/queries/providers";
import styled from "styled-components";
import {
  IReprocessedClaimRow,
  IReprocessedClaimsFilters,
  ReprocessedClaimsSortField,
  ReprocessedClaimsView,
  ReprocessedRecoveryStatus,
} from "types/claim";
import { formatCurrency } from "utils/claimAdjustments";

import {
  amountTone,
  AmountTone,
  BEFORE_FIRST_REMIT_LABEL,
  BEFORE_FIRST_REMIT_TITLE,
  classificationFilterOptions,
  EM_DASH,
  formatCptCodes,
  formatDateOnly,
  formatOriginalClassification,
  formatPercent,
  formatSignedCurrency,
  formatSignedPercent,
  RECOVERY_STATUS_LABELS,
  RECOVERY_STATUS_OPTIONS,
  RECOVERY_STATUS_SEVERITY,
  renderingProviderFilterOptions,
  renderingTaxonomyFilterOptions,
  SOURCE_LABELS,
  UNKNOWN_RECOVERY_TITLE,
  VIEW_OPTIONS,
} from "./reprocessedClaims.utils";

interface IReprocessedClaimsDashboardProps {
  accountId: string;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const EMPTY_MESSAGE = "No reprocessed claims match these filters.";

const TONE_COLORS: Record<AmountTone, string> = {
  positive: "#2e7d32",
  negative: "#c62828",
  neutral: "inherit",
};

const Wrapper = styled.div`
  padding: 0.5rem 0.25rem 2rem;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Eyebrow = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
`;

const Title = styled.h2`
  margin: 0 0 0.35rem;
  font-size: 1.6rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #6c757d;
  max-width: 640px;
`;

const ViewToggle = styled.div`
  margin-bottom: 1.5rem;
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const KpiCard = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 1rem 1.25rem;
  background: #fff;
`;

const KpiLabel = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
`;

const KpiValue = styled.div<{ $tone?: AmountTone }>`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${(props) =>
    props.$tone && props.$tone !== "neutral"
      ? TONE_COLORS[props.$tone]
      : "#212529"};
`;

const KpiCaption = styled.div`
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 0.35rem;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

// CLE-2565 (copied from ClaimsList): a long selected chip (a payer name) must not sit on top of
// the clear "x" — reserve room for the clear icon + chevron and let the chip truncate.
const FilterMultiSelect = styled(MultiSelect)`
  && .p-multiselect-label {
    padding-right: 3.75rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
  && .p-multiselect-token {
    min-width: 0;
    max-width: 100%;
  }
  && .p-multiselect-token .p-multiselect-token-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  && .p-multiselect-clear-icon {
    z-index: 2;
  }
`;

const Signed = styled.span<{ $tone: AmountTone }>`
  color: ${(props) => TONE_COLORS[props.$tone]};
  font-weight: ${(props) => (props.$tone === "neutral" ? 400 : 600)};
  white-space: nowrap;
`;

// One line however many codes the claim has; the full list is in the title.
const CptCell = styled.span`
  display: block;
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// One line, like the CPT cell; the classifier's reason is in the title.
const ClassificationCell = styled.span`
  display: block;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const payerBody = (row: IReprocessedClaimRow) => row.payerName || EM_DASH;

const cptBody = (row: IReprocessedClaimRow) => {
  const codes = formatCptCodes(row.cptCodes || []);
  return <CptCell title={codes}>{codes || EM_DASH}</CptCell>;
};

const originalClassificationBody = (row: IReprocessedClaimRow) => (
  <ClassificationCell
    title={
      (row.originalClassificationCode && row.originalClassificationReason) ||
      undefined
    }
  >
    {formatOriginalClassification(
      row.originalClassificationCode,
      row.originalClassificationTitle
    )}
  </ClassificationCell>
);

const originalDateBody = (row: IReprocessedClaimRow) =>
  row.originalPaymentDate === null ? (
    <span title={BEFORE_FIRST_REMIT_TITLE}>{BEFORE_FIRST_REMIT_LABEL}</span>
  ) : (
    formatDateOnly(row.originalPaymentDate)
  );

const additionalBody = (row: IReprocessedClaimRow) => {
  const tone = amountTone(row.additionalAmountInCents);
  return (
    <Signed $tone={tone} data-tone={tone}>
      {formatSignedCurrency(row.additionalAmountInCents)}
    </Signed>
  );
};

const increaseBody = (row: IReprocessedClaimRow) => {
  const tone = amountTone(row.paymentIncreasePercent);
  return (
    <Signed $tone={tone} data-tone={tone}>
      {formatSignedPercent(row.paymentIncreasePercent)}
    </Signed>
  );
};

const statusBody = (row: IReprocessedClaimRow) => {
  const label =
    RECOVERY_STATUS_LABELS[row.recoveryStatus] || row.recoveryStatus;
  if (row.recoveryStatus === "UNKNOWN") {
    // Neutral grey (as HstImportsTab's "Undone"): not a good or a bad outcome.
    return (
      <Tag
        value={label}
        title={UNKNOWN_RECOVERY_TITLE}
        style={{ background: "var(--gray-500)" }}
      />
    );
  }
  return (
    <Tag
      value={label}
      severity={RECOVERY_STATUS_SEVERITY[row.recoveryStatus]}
    />
  );
};

const sourceBody = (row: IReprocessedClaimRow) =>
  SOURCE_LABELS[row.source] || row.source;

/**
 * CLE-2861 — the Reprocessed Claims page: episodes the payer adjudicated more
 * than once, with what was allowed before and after.
 *
 * Rows come from `GET /claims/reprocessed`, which pages, sorts and computes the
 * summary on the server. Every amount is ALLOWED, not paid. The rows are kept
 * current by the episode rollup. `POST /claims/reprocessed/backfill` still
 * exists for a one-off rescan after a rule change, but has no button.
 *
 * The denial classification shown is that of the ORIGINAL adjudication. The
 * server stores it on each row, so it is filterable like the rendering
 * taxonomy and rendering provider; the options for those three filters come
 * from `GET /claims/reprocessed/filter-options`, computed over the account's
 * whole page population with no filters applied.
 */
export function ReprocessedClaimsDashboard({
  accountId,
}: IReprocessedClaimsDashboardProps) {
  const [view, setView] = useState<ReprocessedClaimsView>("all");
  const [payerIds, setPayerIds] = useState<string[]>([]);
  const [providerIds, setProviderIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<Date[]>([]);
  const [recoveryStatuses, setRecoveryStatuses] = useState<
    ReprocessedRecoveryStatus[]
  >([]);
  const [classificationCodes, setClassificationCodes] = useState<string[]>([]);
  const [renderingTaxonomies, setRenderingTaxonomies] = useState<string[]>([]);
  const [renderingProviderNpis, setRenderingProviderNpis] = useState<string[]>(
    []
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortField, setSortField] = useState<ReprocessedClaimsSortField>(
    "reprocessedPaymentDate"
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [openClaimId, setOpenClaimId] = useState<string | null>(null);

  // Memoized so the RTK Query cache key only changes when a filter does.
  const filters: IReprocessedClaimsFilters = useMemo(
    () => ({
      view,
      payerIds,
      providerIds,
      // A range with only a start picked sends startDate alone.
      startDate: dateRange[0] || null,
      endDate: dateRange[1] || null,
      recoveryStatuses,
      classificationCodes,
      renderingTaxonomies,
      renderingProviderNpis,
      page,
      pageSize,
      sortField,
      sortOrder,
    }),
    [
      view,
      payerIds,
      providerIds,
      dateRange,
      recoveryStatuses,
      classificationCodes,
      renderingTaxonomies,
      renderingProviderNpis,
      page,
      pageSize,
      sortField,
      sortOrder,
    ]
  );
  const hasActiveFilters =
    payerIds.length > 0 ||
    providerIds.length > 0 ||
    dateRange.some(Boolean) ||
    recoveryStatuses.length > 0 ||
    classificationCodes.length > 0 ||
    renderingTaxonomies.length > 0 ||
    renderingProviderNpis.length > 0;

  // `data` is the last result for ANY args, so while a filter change refetches, the KPIs and the
  // table keep the previous values under the loading overlay instead of flashing empty.
  const { data, isFetching, isError } = useGetReprocessedClaimsQuery(
    { accountId, filters },
    { skip: !accountId }
  );
  const { data: clearPayers } = useGetDistinctClearPayersQuery(
    { accountId },
    { skip: !accountId }
  );
  // CLE-2892 — the slim per-account list (id, name, specialty): the full provider read answers 404 to a
  // contracts-only user, who is allowed this tab.
  const { data: providersData } = useGetProviderOptionsByAccountQuery(
    { accountId },
    { skip: !accountId }
  );

  // Same mapping as the Claims list's payer filter: the null payer is the "no-match" sentinel.
  const payerOptions = useMemo(
    () =>
      (clearPayers || []).map((payer) => ({
        label: payer.name,
        value: payer.id === null ? "no-match" : String(payer.id),
      })),
    [clearPayers]
  );
  const providerOptions = useMemo(
    () =>
      (providersData?.data || []).map((provider) => ({
        label: provider.name,
        value: provider.id,
      })),
    [providersData]
  );
  // While these load, or if the request fails, the three filters just have no options.
  const { data: filterOptionsData, isError: isFilterOptionsError } =
    useGetReprocessedClaimsFilterOptionsQuery(
      { accountId },
      { skip: !accountId }
    );
  const filterOptions = isFilterOptionsError ? undefined : filterOptionsData;
  const classificationOptions = useMemo(
    () => classificationFilterOptions(filterOptions),
    [filterOptions]
  );
  const taxonomyOptions = useMemo(
    () => renderingTaxonomyFilterOptions(filterOptions),
    [filterOptions]
  );
  const renderingProviderOptions = useMemo(
    () => renderingProviderFilterOptions(filterOptions),
    [filterOptions]
  );

  const response = isError ? undefined : data;
  const summary = response?.summary;

  // Every view, filter or sort change goes back to page 1: the old page may not exist any more.
  const changeView = (value: ReprocessedClaimsView | null | undefined) => {
    // SelectButton reports null when the active option is clicked again — a view is always chosen.
    if (value == null) {
      return;
    }
    setView(value);
    setPage(1);
  };

  const clearFilters = () => {
    setPayerIds([]);
    setProviderIds([]);
    setDateRange([]);
    setRecoveryStatuses([]);
    setClassificationCodes([]);
    setRenderingTaxonomies([]);
    setRenderingProviderNpis([]);
    setPage(1);
  };

  const onPage = (event: DataTablePFSEvent) => {
    setPage(Math.floor(event.first / event.rows) + 1);
    setPageSize(event.rows);
  };

  const onSort = (event: DataTablePFSEvent) => {
    // Each sortable Column's `sortField` is an API sort key, so the event carries one.
    setSortField(event.sortField as ReprocessedClaimsSortField);
    setSortOrder(event.sortOrder === 1 ? "asc" : "desc");
    setPage(1);
  };

  const claimIdBody = (row: IReprocessedClaimRow) => (
    <Button
      type="button"
      label={row.claimNumber || EM_DASH}
      className="p-button-link p-button-sm p-0 text-left"
      data-testid="reprocessedClaims.claimLink"
      onClick={() => setOpenClaimId(row.claimId)}
    />
  );

  const additionalTone = summary
    ? amountTone(summary.additionalAmountInCents)
    : "neutral";

  return (
    <Wrapper>
      <Header>
        <div>
          <Eyebrow>Revenue Integrity</Eyebrow>
          <Title>Reprocessed Claims</Title>
          <Subtitle>
            Claims the payer adjudicated more than once — what was allowed
            originally, what is allowed now, and the difference.
          </Subtitle>
        </div>
      </Header>

      <ViewToggle>
        <SelectButton
          data-testid="reprocessedClaims.view"
          value={view}
          options={VIEW_OPTIONS}
          optionLabel="label"
          optionValue="value"
          unselectable={false}
          onChange={(e) => changeView(e.value)}
        />
      </ViewToggle>

      <KpiRow>
        <KpiCard>
          <KpiLabel>Reprocessed Claims</KpiLabel>
          <KpiValue data-testid="reprocessedClaims.kpi.claimCount">
            {summary ? summary.claimCount.toLocaleString("en-US") : EM_DASH}
          </KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Original Payments</KpiLabel>
          <KpiValue data-testid="reprocessedClaims.kpi.original">
            {summary ? formatCurrency(summary.originalAmountInCents) : EM_DASH}
          </KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Additional Revenue Recovered</KpiLabel>
          <KpiValue
            $tone={additionalTone}
            data-tone={additionalTone}
            data-testid="reprocessedClaims.kpi.additional"
          >
            {summary
              ? formatSignedCurrency(summary.additionalAmountInCents)
              : EM_DASH}
          </KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Recovery Rate</KpiLabel>
          <KpiValue data-testid="reprocessedClaims.kpi.recoveryRate">
            {summary ? formatPercent(summary.recoveryRatePercent) : EM_DASH}
          </KpiValue>
          <KpiCaption>
            Additional revenue ÷ original underpayment, for claims with an
            expected amount
          </KpiCaption>
        </KpiCard>
      </KpiRow>

      <FilterBar>
        <FilterMultiSelect
          inputId="reprocessedClaimsPayerFilter"
          value={payerIds}
          options={payerOptions}
          // The clear "x" fires onChange with null.
          onChange={(e) => {
            setPayerIds(e.value ?? []);
            setPage(1);
          }}
          placeholder="Payer"
          display="chip"
          filter
          showClear
          maxSelectedLabels={2}
          style={{ minWidth: "200px", maxWidth: "340px" }}
        />
        {providerOptions.length > 1 && (
          <FilterMultiSelect
            inputId="reprocessedClaimsFacilityFilter"
            value={providerIds}
            options={providerOptions}
            onChange={(e) => {
              setProviderIds(e.value ?? []);
              setPage(1);
            }}
            placeholder="Facility"
            display="chip"
            filter
            showClear
            maxSelectedLabels={2}
            style={{ minWidth: "200px", maxWidth: "340px" }}
          />
        )}
        <Calendar
          selectionMode="range"
          placeholder="Reprocessed Date Range"
          // Pass null (not []) when empty — an empty array crashes PrimeReact 8.7.3's range
          // calendar on a month-title click (CLE-2561).
          value={dateRange.length ? dateRange : null}
          onChange={(e: { value: CalendarValueType }) => {
            setDateRange((e.value as Date[]) || []);
            setPage(1);
          }}
          showIcon
          showButtonBar
          dateFormat="mm/dd/yy"
        />
        <FilterMultiSelect
          inputId="reprocessedClaimsStatusFilter"
          value={recoveryStatuses}
          options={RECOVERY_STATUS_OPTIONS}
          onChange={(e) => {
            setRecoveryStatuses(e.value ?? []);
            setPage(1);
          }}
          placeholder="Recovery Status"
          display="chip"
          showClear
          maxSelectedLabels={2}
          style={{ minWidth: "200px", maxWidth: "340px" }}
        />
        <FilterMultiSelect
          inputId="reprocessedClaimsClassificationFilter"
          value={classificationCodes}
          options={classificationOptions}
          onChange={(e) => {
            setClassificationCodes(e.value ?? []);
            setPage(1);
          }}
          placeholder="Original Classification"
          display="chip"
          filter
          showClear
          maxSelectedLabels={2}
          style={{ minWidth: "200px", maxWidth: "340px" }}
        />
        <FilterMultiSelect
          inputId="reprocessedClaimsTaxonomyFilter"
          value={renderingTaxonomies}
          options={taxonomyOptions}
          onChange={(e) => {
            setRenderingTaxonomies(e.value ?? []);
            setPage(1);
          }}
          placeholder="Taxonomy"
          display="chip"
          filter
          showClear
          maxSelectedLabels={2}
          style={{ minWidth: "200px", maxWidth: "340px" }}
        />
        <FilterMultiSelect
          inputId="reprocessedClaimsRenderingProviderFilter"
          value={renderingProviderNpis}
          options={renderingProviderOptions}
          onChange={(e) => {
            setRenderingProviderNpis(e.value ?? []);
            setPage(1);
          }}
          placeholder="Rendering Provider"
          display="chip"
          filter
          showClear
          maxSelectedLabels={2}
          style={{ minWidth: "200px", maxWidth: "340px" }}
        />
        {hasActiveFilters && (
          <Button
            label="Clear filters"
            className="p-button-text p-button-sm"
            onClick={clearFilters}
          />
        )}
      </FilterBar>

      {isError ? (
        <KpiCard role="alert">
          Couldn&apos;t load reprocessed claims. Try again in a moment.
        </KpiCard>
      ) : (
        <DataTable
          value={response?.rows || []}
          dataKey="claimId"
          lazy
          paginator
          first={(page - 1) * pageSize}
          rows={pageSize}
          totalRecords={response?.total || 0}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onPage={onPage}
          onSort={onSort}
          sortField={sortField}
          sortOrder={sortOrder === "asc" ? 1 : -1}
          // While loading, PrimeReact shows the overlay and no empty-message row, so "no rows" is
          // only ever said about a response for the current filters.
          loading={isFetching}
          emptyMessage={EMPTY_MESSAGE}
          size="small"
          stripedRows
          responsiveLayout="scroll"
        >
          <Column
            header="Payer"
            body={payerBody}
            sortable
            sortField="payerName"
          />
          <Column
            header="Claim ID"
            body={claimIdBody}
            sortable
            sortField="claimNumber"
          />
          <Column
            header="Patient Name"
            field="patientName"
            sortable
            sortField="patientName"
          />
          <Column header="CPT Code" body={cptBody} />
          <Column
            header="Original Denial Classification"
            body={originalClassificationBody}
          />
          <Column
            header="Original Payment Date"
            body={originalDateBody}
            sortable
            sortField="originalPaymentDate"
          />
          <Column
            header="Original Payment Amount"
            body={(row: IReprocessedClaimRow) =>
              formatCurrency(row.originalAmountInCents)
            }
            sortable
            sortField="originalAmount"
          />
          <Column
            header="Reprocessed Payment Date"
            body={(row: IReprocessedClaimRow) =>
              formatDateOnly(row.reprocessedPaymentDate)
            }
            sortable
            sortField="reprocessedPaymentDate"
          />
          <Column
            header="Final Payment Amount"
            body={(row: IReprocessedClaimRow) =>
              formatCurrency(row.finalAmountInCents)
            }
            sortable
            sortField="finalAmount"
          />
          <Column
            header="Additional Revenue Recovered"
            body={additionalBody}
            sortable
            sortField="additionalAmount"
          />
          <Column
            header="Payment Increase %"
            body={increaseBody}
            sortable
            sortField="paymentIncreasePercent"
          />
          <Column
            header="Recovery Status"
            body={statusBody}
            sortable
            sortField="recoveryStatus"
          />
          {view === "all" && <Column header="Source" body={sourceBody} />}
        </DataTable>
      )}

      {/* The existing claim modal — its Remit Details tab already lists the original
          adjudication and every later event in order. */}
      {openClaimId && (
        <RemitClaimModal
          claimId={openClaimId}
          accountId={accountId}
          onHide={() => setOpenClaimId(null)}
        />
      )}
    </Wrapper>
  );
}
